import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@nexply/ui';
import { useAuth } from '../lib/auth.js';
import { FloatingField } from '../components/FloatingField.js';
import { BrandSide, nextSearch, nextTarget } from './login.js';
import { LegalFooter } from '../components/LegalFooter.js';

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register(email, password, firstName, lastName);
      await nav({ to: nextTarget() });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      setError(
        e.code === 'email_taken'
          ? 'Diese E-Mail ist bereits registriert.'
          : e.message ?? 'Registrierung fehlgeschlagen.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-ink lg:grid lg:grid-cols-[1.2fr_1fr] xl:grid-cols-[1.4fr_1fr]">
      <BrandSide />

      <div className="relative z-10 flex min-h-dvh w-full flex-col">
        <div className="lg:hidden pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-gold/15 blur-[120px]" />
        <div className="lg:hidden pointer-events-none absolute bottom-0 right-0 h-[280px] w-[280px] rounded-full bg-gold/10 blur-[100px]" />

        <div className="relative z-10 mx-auto flex w-full max-w-sm flex-1 flex-col px-6 pt-[calc(env(safe-area-inset-top)+48px)] pb-[calc(env(safe-area-inset-bottom)+24px)] lg:max-w-md lg:justify-center lg:py-12 lg:px-10">
          <div className="text-center mb-10 lg:hidden animate-slide-up">
            <h1 className="text-6xl font-semibold tracking-tighter text-gold-grad">nexply</h1>
            <p className="mt-3 text-[11px] uppercase tracking-[0.4em] text-gold/70">Life is a Puzzle</p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-text">Konto erstellen</h2>
              <p className="mt-1 text-sm text-text-dim">Werde Teil des nexply-Netzwerks.</p>
            </div>

            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <FloatingField id="firstName" label="Vorname" autoComplete="given-name" value={firstName} onChange={setFirstName} required />
                <FloatingField id="lastName" label="Nachname" autoComplete="family-name" value={lastName} onChange={setLastName} required />
              </div>
              <FloatingField id="email" label="E-Mail" type="email" autoComplete="email" value={email} onChange={setEmail} required />
              <FloatingField id="password" label="Passwort" type="password" autoComplete="new-password" minLength={8} value={password} onChange={setPassword} required hint="Mindestens 8 Zeichen." />
            </div>

            <label className="flex cursor-pointer items-start gap-3 text-xs text-text-dim">
              <input
                type="checkbox"
                required
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-gold"
              />
              <span>
                Ich stimme den{' '}
                <a href="/agb" target="_blank" rel="noopener" className="text-gold-light hover:text-gold underline">
                  Nutzungsbedingungen
                </a>{' '}
                zu und habe die{' '}
                <a href="/datenschutz" target="_blank" rel="noopener" className="text-gold-light hover:text-gold underline">
                  Datenschutzerklärung
                </a>{' '}
                zur Kenntnis genommen.
              </span>
            </label>

            {error && (
              <div className="text-xs text-danger bg-danger/10 border border-danger/25 rounded-xl px-4 py-3">
                {error}
              </div>
            )}

            <Button type="submit" size="lg" fullWidth loading={busy}>
              Registrieren
            </Button>

            <div className="mt-auto lg:mt-2 pt-8 lg:pt-4 text-center text-sm text-text-dim">
              Schon registriert?{' '}
              <Link to="/login" search={nextSearch()} className="font-medium text-gold-light hover:text-gold transition">
                Einloggen
              </Link>
            </div>
          </form>
          <LegalFooter className="mt-8 lg:mt-6" />
        </div>
      </div>
    </div>
  );
}
